import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Target, DollarSign, Calendar, MoreVertical } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Quote } from '@/types'
import { formatCurrency, formatDate } from '@/lib/utils'

interface MobileQuoteCardProps {
  quote: Quote
  isSelected: boolean
  onToggleSelect: (id: string) => void
}

function getStatusVariant(status?: string) {
  switch (status) {
    case 'Accepted':
      return 'success'
    case 'Rejected':
      return 'destructive'
    case 'Sent':
      return 'default'
    case 'Draft':
    default:
      return 'secondary'
  }
}

export function MobileQuoteCard({ quote, isSelected, onToggleSelect }: MobileQuoteCardProps) {
  const displayName = quote.name || quote.quote_name || quote.quote_number

  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(quote.id)}
              aria-label={`Select ${displayName}`}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <Link
                to={`/quotes/${quote.id}`}
                className="font-semibold text-base hover:underline focus:underline focus:outline-none"
              >
                {displayName}
              </Link>
              {quote.status && (
                <Badge variant={getStatusVariant(quote.status)} className="mt-1 text-xs">
                  {quote.status}
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Actions for ${displayName}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/quotes/${quote.id}`}>View Details</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {quote.opportunity_name && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Target className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="truncate">{quote.opportunity_name}</span>
          </div>
        )}

        {quote.total_price !== undefined && quote.total_price !== null && (
          <div className="flex items-center gap-2">
            <DollarSign
              className="h-4 w-4 flex-shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="font-medium">
              {formatCurrency(quote.total_price, quote.currency_iso_code || 'CAD')}
            </span>
          </div>
        )}

        {quote.expiration_date && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>Expires: {formatDate(quote.expiration_date)}</span>
          </div>
        )}

        {quote.created_date && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span className="text-xs">Created {formatDate(quote.created_date)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface MobileQuoteListProps {
  quotes: Quote[]
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
}

export function MobileQuoteList({ quotes, selectedIds, onToggleSelect }: MobileQuoteListProps) {
  return (
    <div className="space-y-3">
      {quotes.map((quote) => (
        <MobileQuoteCard
          key={quote.id}
          quote={quote}
          isSelected={selectedIds.has(quote.id)}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  )
}
